import { useState } from "react";
import { Alert, Box, Button, CircularProgress, Divider, Link, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import AppleIcon from "@mui/icons-material/Apple";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { Link as RouterLink } from "react-router";
import { useAuth } from "src/context/AuthContext";
import { useLang, useT, useTRich } from "src/i18n";
import { resolveErrorMessage } from "src/i18n/errorCopy";
import { loginWithMicrosoftRedirect } from "src/auth/msal";
import { ensureAppleReady } from "src/auth/apple";
import { buildLinkedInAuthUrl, LINKEDIN_CALLBACK_PATH } from "src/auth/linkedin";
import { captureError } from "src/utils/errorReporting";
import { KAYTTOEHDOT_ROUTE, TIETOSUOJA_ROUTE } from "src/utils/quote/quoteTerms";
import { reaffirmAcceptances } from "src/data/legalDocument";
import { providerButtonSx, getTextPrimary } from "src/components/providerButtonSx";

// 340 keeps the GIS iframe inside DialogContent at sm (Dialog xs paper ≈ 444 - 80 padding ≈ 364).
const SOCIAL_BUTTON_MAX = 340;

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || "";

export default function SocialLoginButtons() {
    const { loginWithGoogle, loginWithApple } = useAuth();
    const { lang } = useLang();
    const t = useT();
    const tRich = useTRich();
    const theme = useTheme();
    const textPrimary = getTextPrimary(theme);
    const spinnerColor = alpha(textPrimary, 0.8);
    const [error, setError] = useState(null);
    // null when idle, otherwise the provider currently being authenticated.
    // Drives both the global disable (all buttons disable during any flow)
    // and the per-button spinner/label swap so the user sees which provider
    // is in flight during the silent backend roundtrip after popup-close.
    const [pending, setPending] = useState(null);
    const busy = pending !== null;

    const handleGoogleSuccess = async (response) => {
        if (!response?.credential) return;
        setError(null);
        setPending("google");
        try {
            const authData = await loginWithGoogle(response.credential);
            reaffirmAcceptances(authData);
        } catch (err) {
            captureError(err, { tags: { auth: "google" } });
            setError(resolveErrorMessage(err, { t, lang, fallbackKey: "auth.googleLoginFailed" }));
        } finally {
            setPending(null);
        }
    };

    const handleMicrosoftClick = async () => {
        setError(null);
        setPending("microsoft");
        try {
            await loginWithMicrosoftRedirect();
        } catch (err) {
            if (err?.errorCode === "user_cancelled") {
                // user-cancelled popup/redirect chooser is not worth surfacing
            } else if (err?.errorCode === "interaction_in_progress") {
                setError(t("auth.social.microsoftInProgress"));
            } else {
                captureError(err, { tags: { auth: "microsoft" } });
                setError(resolveErrorMessage(err, { t, lang, fallbackKey: "auth.microsoftLoginFailed" }));
            }
            setPending(null);
        }
    };

    const handleAppleClick = async () => {
        setError(null);
        setPending("apple");
        try {
            await ensureAppleReady(t);
            const res = await window.AppleID.auth.signIn();
            const idToken = res?.authorization?.id_token;
            if (!idToken) throw new Error(t("auth.social.appleNoToken"));
            const authData = await loginWithApple({
                idToken,
                firstName: res.user?.name?.firstName,
                lastName: res.user?.name?.lastName,
            });
            reaffirmAcceptances(authData);
        } catch (err) {
            if (err?.error === "popup_closed_by_user") {
                // user cancelled — silent, mirrors MSAL user_cancelled
            } else {
                captureError(err, { tags: { auth: "apple" } });
                setError(resolveErrorMessage(err, { t, lang, fallbackKey: "auth.appleLoginFailed" }));
            }
        } finally {
            setPending(null);
        }
    };

    const handleLinkedInClick = async () => {
        setError(null);
        setPending("linkedin");
        try {
            const url = await buildLinkedInAuthUrl({
                redirectUri: window.location.origin + LINKEDIN_CALLBACK_PATH,
                source: "betonijerry",
                t,
            });
            window.location.assign(url);
            // page navigates away; no need to clear pending state
        } catch (err) {
            setPending(null);
            captureError(err, { tags: { auth: "linkedin" } });
            setError(resolveErrorMessage(err, { t, lang, fallbackKey: "auth.linkedinLoginFailed" }));
        }
    };

    return (
        <Stack spacing={2}>
            <Box sx={{ my: 1 }}>
                <Divider>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {t("auth.social.orContinueWith")}
                    </Typography>
                </Divider>
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
            <Stack
                direction="column"
                spacing={1.5}
                sx={{ width: "100%", maxWidth: SOCIAL_BUTTON_MAX, mx: "auto" }}
            >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    {/* GoogleLogin reads its `locale` from GoogleOAuthProvider's context, and
                        that context is memoized on mount — a `locale` prop change alone does
                        NOT re-render the already-drawn Google button (nor reload the GSI
                        script's own `?hl=` locale). Keying the provider on `lang` forces a
                        full remount so the button text actually follows the FI/EN toggle.
                        Scoped here (not at the app root, main.jsx) so a language switch never
                        remounts AuthProvider/App and drops session state. */}
                    <GoogleOAuthProvider key={lang} clientId={GOOGLE_CLIENT_ID} locale={lang}>
                        <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={(error) => {
                                captureError(error ?? new Error("GoogleLogin onError fired with no error"), {
                                    tags: { auth: "google" },
                                });
                                setError(t("auth.googleLoginFailed"));
                            }}
                            useOneTap={false}
                            text="continue_with"
                            theme="filled_black"
                            shape="rectangular"
                            size="large"
                            width={String(SOCIAL_BUTTON_MAX)}
                        />
                    </GoogleOAuthProvider>
                </Box>
                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={pending === "microsoft"
                        ? <CircularProgress size={18} sx={{ color: spinnerColor }} />
                        : <MicrosoftIcon />}
                    onClick={handleMicrosoftClick}
                    disabled={busy}
                    sx={providerButtonSx(theme, pending === "microsoft")}
                >
                    {pending === "microsoft" ? t("auth.shared.loggingIn") : t("auth.social.continueWithMicrosoft")}
                </Button>
                {import.meta.env.VITE_APPLE_CLIENT_ID && (
                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        startIcon={pending === "apple"
                            ? <CircularProgress size={18} sx={{ color: spinnerColor }} />
                            : <AppleIcon />}
                        onClick={handleAppleClick}
                        disabled={busy}
                        sx={providerButtonSx(theme, pending === "apple")}
                    >
                        {pending === "apple" ? t("auth.shared.loggingIn") : t("auth.social.continueWithApple")}
                    </Button>
                )}
                {import.meta.env.VITE_LINKEDIN_CLIENT_ID && (
                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        startIcon={pending === "linkedin"
                            ? <CircularProgress size={18} sx={{ color: spinnerColor }} />
                            : <LinkedInIcon />}
                        onClick={handleLinkedInClick}
                        disabled={busy}
                        sx={providerButtonSx(theme, pending === "linkedin")}
                    >
                        {pending === "linkedin" ? t("auth.shared.loggingIn") : t("auth.social.continueWithLinkedin")}
                    </Button>
                )}
                <Typography
                    variant="caption"
                    sx={{ display: "block", textAlign: "center", color: "text.secondary", fontSize: 11, lineHeight: 1.5, mt: 0.5 }}
                >
                    {tRich("auth.social.legalConsent", {
                        terms: (
                            <Link component={RouterLink} to={KAYTTOEHDOT_ROUTE} target="_blank" rel="noopener" underline="hover" color="inherit">
                                {t("auth.social.termsLinkText")}
                            </Link>
                        ),
                        privacy: (
                            <Link component={RouterLink} to={TIETOSUOJA_ROUTE} target="_blank" rel="noopener" underline="hover" color="inherit">
                                {t("auth.social.privacyLinkText")}
                            </Link>
                        ),
                    })}
                </Typography>
            </Stack>
        </Stack>
    );
}
